import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { assetUrl } from './assetUrl';
import { CHARACTER_HEIGHT } from './characterSize';
import {
  ArmChain,
  ArmSide,
  findArmChain,
  solveTwoBoneIK,
} from './HandHold';

/**
 * Character visual + animation.
 * Prefers Mixamo GLB (`/models/hero.glb` / `companion.glb`) with AnimationMixer.
 * Falls back to procedural cloaked figure until / unless the GLB loads.
 *
 * Hand-hold: Mixamo has no dedicated couple Hold Hands walk. We ship HoldIdle /
 * HoldWalk (object-hold arm poses) plus runtime two-bone IK so hands meet.
 */
export type AnimState =
  | 'idle'
  | 'walk'
  | 'run'
  | 'jump'
  | 'land'
  | 'climb'
  | 'climbOff'
  | 'fall'
  | 'holdIdle'
  | 'holdWalk'
  | 'holdRun'
  | 'takeHand'
  | 'wallIdle'
  | 'shimmyL'
  | 'shimmyR'
  | 'pull'
  | 'wave'
  | 'greet'
  | 'talk'
  | 'lookAround'
  | 'yawn'
  | 'stretch'
  | 'victory'
  | 'afraid'
  | 'crouch'
  | 'sneak'
  | 'point'
  | 'kneel'
  | 'cry'
  | 'clap'
  | 'hit'
  | 'block'
  | 'angry'
  | 'think'
  | 'agree'
  | 'dismiss'
  | 'breathe'
  | 'hang'
  | 'throw';

const STATE_TO_CLIP: Record<AnimState, string> = {
  idle: 'Idle',
  walk: 'Walk',
  run: 'Run',
  jump: 'Jump',
  land: 'Land',
  climb: 'Climb',
  climbOff: 'ClimbOff',
  fall: 'Fall',
  holdIdle: 'Idle',
  holdWalk: 'HoldWalk',
  holdRun: 'HoldWalk',
  takeHand: 'TakeHand',
  wallIdle: 'WallIdle',
  shimmyL: 'ShimmyL',
  shimmyR: 'ShimmyR',
  pull: 'Pull',
  wave: 'Wave',
  greet: 'Greet',
  talk: 'Talk',
  lookAround: 'LookAround',
  yawn: 'Yawn',
  stretch: 'Stretch',
  victory: 'Victory',
  afraid: 'Afraid',
  crouch: 'Crouch',
  sneak: 'Sneak',
  point: 'Point',
  kneel: 'Kneel',
  cry: 'Cry',
  clap: 'Clap',
  hit: 'Hit',
  block: 'Block',
  angry: 'Angry',
  think: 'Think',
  agree: 'Agree',
  dismiss: 'Dismiss',
  breathe: 'Breathe',
  hang: 'Hang',
  throw: 'Throw',
};

/** When a preferred clip is missing, fall back so locomotion never freezes. */
const STATE_FALLBACK: Partial<Record<AnimState, AnimState>> = {
  holdWalk: 'walk',
  holdRun: 'run',
  fall: 'jump',
  climbOff: 'jump',
  land: 'idle',
  wallIdle: 'idle',
  shimmyL: 'walk',
  shimmyR: 'walk',
  takeHand: 'idle',
  pull: 'idle',
  wave: 'idle',
  greet: 'idle',
  talk: 'idle',
  lookAround: 'idle',
  yawn: 'idle',
  stretch: 'idle',
  victory: 'idle',
  afraid: 'idle',
  crouch: 'idle',
  sneak: 'walk',
  point: 'idle',
  kneel: 'idle',
  cry: 'idle',
  clap: 'idle',
  hit: 'idle',
  block: 'idle',
  angry: 'idle',
  think: 'idle',
  agree: 'idle',
  dismiss: 'idle',
  breathe: 'idle',
  hang: 'climb',
  throw: 'idle',
};

const FADE = 0.22;
const TAKE_HAND_DURATION = 0.72;
const ONESHOT_STATES: AnimState[] = [
  'jump',
  'takeHand',
  'climbOff',
  'land',
  'pull',
  'wave',
  'greet',
  'victory',
  'yawn',
  'stretch',
  'hit',
  'clap',
  'agree',
  'dismiss',
  'angry',
  'throw',
];

/**
 * Measure bind-pose mesh height in *local geometry* space.
 * Mixamo GLBs from Blender ship `MixamoRig` at scale 0.01 (cm→m for bones).
 * `Box3.setFromObject` then reports ~1.7cm and a naive normalize blows the
 * character up ~100×. Skinned draw size already follows the 0.01 bone path, so
 * we must scale from geometry meters, not the shrunken world AABB.
 */
function measureLocalMeshBounds(root: THREE.Object3D): THREE.Box3 {
  const union = new THREE.Box3();
  let found = false;
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    const bb = mesh.geometry.boundingBox;
    if (!bb || bb.isEmpty()) return;
    if (!found) {
      union.copy(bb);
      found = true;
    } else {
      union.union(bb);
    }
  });
  if (!found) union.set(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 1.7, 0));
  return union;
}

/** World-space AABB of skinned meshes after matrices are current. */
function measureWorldMeshBounds(root: THREE.Object3D): THREE.Box3 {
  const union = new THREE.Box3();
  let found = false;
  const tmp = new THREE.Box3();
  root.traverse((obj) => {
    const mesh = obj as THREE.SkinnedMesh;
    if (mesh.isSkinnedMesh) {
      mesh.computeBoundingBox();
      if (mesh.boundingBox && !mesh.boundingBox.isEmpty()) {
        tmp.copy(mesh.boundingBox).applyMatrix4(mesh.matrixWorld);
        if (!found) {
          union.copy(tmp);
          found = true;
        } else {
          union.union(tmp);
        }
        return;
      }
    }
    const m = obj as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
    if (!m.geometry.boundingBox) return;
    tmp.copy(m.geometry.boundingBox).applyMatrix4(m.matrixWorld);
    if (!found) {
      union.copy(tmp);
      found = true;
    } else {
      union.union(tmp);
    }
  });
  if (!found) {
    union.set(
      new THREE.Vector3(-0.2, 0, -0.2),
      new THREE.Vector3(0.2, CHARACTER_HEIGHT, 0.2),
    );
  }
  return union;
}

function makeNoiseTexture(hex: string, variance = 0.1): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const img = ctx.createImageData(size, size);
  for (let i = 0; i < size * size; i++) {
    const n = (Math.random() * 2 - 1) * variance * 255;
    const o = i * 4;
    img.data[o] = Math.max(0, Math.min(255, r + n));
    img.data[o + 1] = Math.max(0, Math.min(255, g + n));
    img.data[o + 2] = Math.max(0, Math.min(255, b + n));
    img.data[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

type ProceduralParts = {
  hips: THREE.Group;
  leftArm: THREE.Group;
  rightArm: THREE.Group;
  leftLeg: THREE.Group;
  rightLeg: THREE.Group;
  cloak: THREE.Mesh;
};

export class CharacterRig {
  readonly root = new THREE.Group();
  private phase = 0;
  private state: AnimState = 'idle';
  private glow?: THREE.PointLight;
  private readonly companion: boolean;
  private procedural: ProceduralParts | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private actions = new Map<AnimState, THREE.AnimationAction>();
  private namedActions = new Map<string, THREE.AnimationAction>();
  private clipNames: string[] = [];
  private currentAction: THREE.AnimationAction | null = null;
  private mixamoReady = false;
  private testLock = false;
  private loadPromise: Promise<boolean>;
  private leftArm: ArmChain | null = null;
  private rightArm: ArmChain | null = null;
  private holdWeight = 0;
  private takeHandTimer = 0;
  private heldMode = false;
  private ikSide: ArmSide = 'left';
  private readonly ikTarget = new THREE.Vector3();
  private ikActive = false;
  private readonly handWorld = new THREE.Vector3();

  constructor(opts: { companion?: boolean; modelUrl?: string } = {}) {
    this.companion = !!opts.companion;
    // Companion offers right hand; player leads with left (ICO-style pair).
    this.ikSide = this.companion ? 'right' : 'left';
    this.buildProcedural();

    const url =
      opts.modelUrl ??
      (this.companion ? assetUrl('models/companion.glb') : assetUrl('models/hero.glb'));
    this.loadPromise = this.tryLoadMixamo(url);
  }

  /** Resolves true when Mixamo model is active. */
  whenReady(): Promise<boolean> {
    return this.loadPromise;
  }

  isMixamo(): boolean {
    return this.mixamoReady;
  }

  private buildProcedural(): void {
    const skinMap = makeNoiseTexture(this.companion ? '#e8d4b8' : '#c4a882', 0.08);
    const clothMap = makeNoiseTexture(this.companion ? '#d4c09a' : '#3d342c', 0.14);
    const hornMap = makeNoiseTexture('#e8dcc8', 0.05);

    const skin = new THREE.MeshStandardMaterial({
      map: skinMap,
      color: 0xffffff,
      roughness: 0.78,
      metalness: 0.05,
    });
    const cloth = new THREE.MeshStandardMaterial({
      map: clothMap,
      color: 0xffffff,
      roughness: 0.88,
      metalness: 0.02,
      side: THREE.DoubleSide,
      emissive: this.companion ? 0x000000 : 0x1a1208,
      emissiveIntensity: this.companion ? 0 : 0.12,
    });
    const hornMat = new THREE.MeshStandardMaterial({
      map: hornMap,
      color: 0xffffff,
      roughness: 0.4,
      metalness: 0.15,
      emissive: this.companion ? 0xffd9a0 : 0x000000,
      emissiveIntensity: this.companion ? 0.55 : 0,
    });

    const hips = new THREE.Group();
    hips.name = 'procedural';
    hips.position.y = 0.95;
    this.root.add(hips);

    const pelvis = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.18, 0.22), cloth);
    hips.add(pelvis);

    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.48, 0.24), cloth);
    torso.position.y = 0.32;
    hips.add(torso);

    const cloak = new THREE.Mesh(
      new THREE.ConeGeometry(0.42, 0.85, 8, 1, true),
      cloth,
    );
    cloak.position.set(0, 0.05, -0.05);
    cloak.rotation.x = Math.PI;
    hips.add(cloak);

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 10), skin);
    head.position.y = 0.68;
    hips.add(head);

    if (!this.companion) {
      const hornL = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.22, 6), hornMat);
      hornL.position.set(-0.1, 0.82, 0);
      hornL.rotation.z = 0.35;
      const hornR = hornL.clone();
      hornR.position.x = 0.1;
      hornR.rotation.z = -0.35;
      hips.add(hornL, hornR);
    } else {
      const aura = new THREE.Mesh(
        new THREE.SphereGeometry(0.22, 16, 12),
        new THREE.MeshBasicMaterial({
          color: 0xffe2b0,
          transparent: true,
          opacity: 0.22,
          depthWrite: false,
        }),
      );
      aura.position.y = 0.68;
      hips.add(aura);
    }

    const leftArm = this.makeLimb(skin, cloth, -1);
    const rightArm = this.makeLimb(skin, cloth, 1);
    const leftLeg = this.makeLeg(cloth, -1);
    const rightLeg = this.makeLeg(cloth, 1);
    hips.add(leftArm, rightArm, leftLeg, rightLeg);

    this.procedural = { hips, leftArm, rightArm, leftLeg, rightLeg, cloak };

    if (this.companion) {
      this.glow = new THREE.PointLight(0xffd4a0, 1.4, 8, 2);
      this.glow.position.y = 1.2;
      this.root.add(this.glow);
    }

    this.root.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
  }

  private makeLimb(skin: THREE.Material, cloth: THREE.Material, side: number): THREE.Group {
    const g = new THREE.Group();
    g.position.set(side * 0.24, 0.48, 0);
    const upper = new THREE.Mesh(new THREE.CapsuleGeometry(0.055, 0.22, 4, 8), cloth);
    upper.position.y = -0.14;
    const lower = new THREE.Mesh(new THREE.CapsuleGeometry(0.045, 0.2, 4, 8), skin);
    lower.position.y = -0.38;
    g.add(upper, lower);
    return g;
  }

  private makeLeg(cloth: THREE.Material, side: number): THREE.Group {
    const g = new THREE.Group();
    g.position.set(side * 0.1, -0.05, 0);
    const thigh = new THREE.Mesh(new THREE.CapsuleGeometry(0.07, 0.28, 4, 8), cloth);
    thigh.position.y = -0.2;
    const shin = new THREE.Mesh(new THREE.CapsuleGeometry(0.055, 0.28, 4, 8), cloth);
    shin.position.y = -0.52;
    g.add(thigh, shin);
    return g;
  }

  private async tryLoadMixamo(url: string): Promise<boolean> {
    try {
      const loader = new GLTFLoader();
      const gltf = await loader.loadAsync(url);
      this.installMixamo(gltf.scene, gltf.animations);
      return true;
    } catch (err) {
      console.warn(
        `[CharacterRig] Mixamo load failed (${url}); keeping procedural.`,
        err,
      );
      return false;
    }
  }

  private installMixamo(scene: THREE.Group, clips: THREE.AnimationClip[]): void {
    // Remove procedural meshes (keep companion glow light on root).
    // Don't dispose textures/materials immediately — the renderer may still
    // reference them for a frame (causes "no image data" spam).
    for (const child of [...this.root.children]) {
      if (child === this.glow) continue;
      this.root.remove(child);
    }
    this.procedural = null;

    const wrapper = new THREE.Group();
    wrapper.name = 'mixamo';
    wrapper.add(scene);

    // Local geometry is ~1.7–1.8 m. Armature often has scale 0.01 for cm bones;
    // Box3.setFromObject then reports ~cm height → naive normalize ≈100× blow-up.
    // Always normalize from local mesh meters; skinned draw already includes 0.01.
    const local = measureLocalMeshBounds(scene);
    const localHeight = Math.max(local.max.y - local.min.y, 1e-3);
    const scale = CHARACTER_HEIGHT / localHeight;
    wrapper.scale.setScalar(scale);
    console.warn(
      `[CharacterRig] ${this.companion ? 'companion' : 'hero'} scale=${scale.toFixed(4)} ` +
        `(localMeshH=${localHeight.toFixed(3)}m → ${CHARACTER_HEIGHT}m)`,
    );

    // Feet on ground: use geometry min.y under armature world (skinned probe).
    wrapper.updateMatrixWorld(true);
    scene.traverse((obj) => {
      const sk = obj as THREE.SkinnedMesh;
      if (sk.isSkinnedMesh) {
        sk.skeleton.bones.forEach((b) => b.updateMatrixWorld(true));
        sk.computeBoundingBox();
      }
    });
    const grounded = measureWorldMeshBounds(wrapper);
    let footY = grounded.min.y;
    // If probe collapsed under 0.01×, fall back to scaled local min.
    if (!Number.isFinite(footY) || Math.abs(footY) > CHARACTER_HEIGHT * 2) {
      footY = local.min.y * scale;
    }
    // When world probe is ~cm (0.01×), feet offset must use the same space as scale.
    if (Math.abs(grounded.max.y - grounded.min.y) < 0.45) {
      footY = local.min.y * scale;
    }
    wrapper.position.y -= footY;

    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const mat of mats) {
          if (!(mat instanceof THREE.MeshStandardMaterial)) continue;
          if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace;
          mat.roughness = Math.min(0.95, Math.max(0.45, mat.roughness ?? 0.7));
          mat.envMapIntensity = 0.85;
          if (this.companion) {
            mat.emissive = new THREE.Color(0xffd4a0);
            mat.emissiveIntensity = 0.12;
          }
        }
      }
    });

    this.root.add(wrapper);

    this.mixer = new THREE.AnimationMixer(scene);
    this.actions.clear();
    this.namedActions.clear();
    this.clipNames = clips.map((c) => c.name).sort((a, b) => a.localeCompare(b));

    const byName = new Map(clips.map((c) => [c.name, c]));
    for (const [state, clipName] of Object.entries(STATE_TO_CLIP) as [AnimState, string][]) {
      const clip = byName.get(clipName);
      if (!clip) continue;
      const action = this.mixer.clipAction(clip);
      action.enabled = true;
      action.setEffectiveWeight(1);
      if (ONESHOT_STATES.includes(state)) {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      } else {
        action.setLoop(THREE.LoopRepeat, Infinity);
      }
      this.actions.set(state, action);
      this.namedActions.set(clipName, action);
    }
    // Also index every packed clip for the anim tester
    for (const clip of clips) {
      if (this.namedActions.has(clip.name)) continue;
      const action = this.mixer.clipAction(clip);
      action.enabled = true;
      this.namedActions.set(clip.name, action);
    }

    this.leftArm = findArmChain(scene, 'left');
    this.rightArm = findArmChain(scene, 'right');

    this.mixamoReady = true;
    const start = this.actions.get(this.state) ?? this.actions.get('idle');
    if (start) {
      start.reset().fadeIn(0.01).play();
      this.currentAction = start;
    }

    if (this.glow) {
      this.glow.position.y = CHARACTER_HEIGHT * 0.72;
    }
  }

  listClipNames(): string[] {
    // Hide joke / combat / toilet Mixamo leftovers from the anim tester cycle
    const skip = /toilet|zombie|death|swim|rifle|pistol|gun|crawl|drunk|yelling|sitting/i;
    return this.clipNames.filter((n) => !skip.test(n));
  }

  /** Force-play any packed Mixamo clip by name (anim tester). */
  playClipByName(name: string): boolean {
    if (!this.mixer) return false;
    const action = this.namedActions.get(name);
    if (!action) return false;
    this.testLock = true;
    action.reset();
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.clampWhenFinished = false;
    action.setEffectiveWeight(1);
    if (this.currentAction && this.currentAction !== action) {
      this.currentAction.fadeOut(FADE);
      action.fadeIn(FADE).play();
    } else {
      action.fadeIn(0.05).play();
    }
    this.currentAction = action;
    return true;
  }

  clearTestLock(): void {
    this.testLock = false;
  }

  isTestLocked(): boolean {
    return this.testLock;
  }

  /** Enter take-hand pose, then caller switches to holdIdle/holdWalk. */
  beginTakeHand(): void {
    this.heldMode = true;
    this.takeHandTimer = TAKE_HAND_DURATION;
    this.setState('takeHand');
  }

  setHeldMode(v: boolean): void {
    this.heldMode = v;
    if (!v) {
      this.takeHandTimer = 0;
      this.ikActive = false;
      this.holdWeight = Math.min(this.holdWeight, 0.35);
    }
  }

  isTakingHand(): boolean {
    return this.takeHandTimer > 0;
  }

  isHeldMode(): boolean {
    return this.heldMode;
  }

  /** World-space palm position for the hold side (or opposite). */
  getHandWorldPosition(side: ArmSide = this.ikSide, out = this.handWorld): THREE.Vector3 {
    const chain = side === 'left' ? this.leftArm : this.rightArm;
    if (chain) {
      chain.hand.getWorldPosition(out);
      return out;
    }
    // Procedural approx: hip-height reach.
    out.set(side === 'left' ? -0.35 : 0.35, CHARACTER_HEIGHT * 0.55, 0.15);
    this.root.localToWorld(out);
    return out;
  }

  /** After both characters update, pull this character's hold-hand toward a world target. */
  setHandIkTarget(worldPos: THREE.Vector3 | null): void {
    if (!worldPos) {
      this.ikActive = false;
      return;
    }
    this.ikTarget.copy(worldPos);
    this.ikActive = true;
  }

  setState(next: AnimState): void {
    if (this.testLock) return;
    if (next === this.state) return;
    // Don't interrupt take-hand until it finishes (except release / climb / air).
    if (
      this.takeHandTimer > 0 &&
      next !== 'takeHand' &&
      next !== 'climb' &&
      next !== 'jump' &&
      next !== 'fall' &&
      next !== 'idle'
    ) {
      // Allow hold locomotion only after take completes.
      if (next === 'holdIdle' || next === 'holdWalk' || next === 'holdRun') {
        return;
      }
    }
    this.state = next;
    if (!this.mixamoReady || !this.mixer) return;

    const nextAction = this.resolveAction(next);
    if (!nextAction) return;

    if (this.currentAction === nextAction && next !== 'takeHand' && !ONESHOT_STATES.includes(next)) {
      return;
    }

    nextAction.reset();
    if (ONESHOT_STATES.includes(next)) {
      nextAction.setLoop(THREE.LoopOnce, 1);
      nextAction.clampWhenFinished = true;
      nextAction.setEffectiveTimeScale(
        next === 'takeHand' ? 1.45 : next === 'pull' ? 0.95 : next === 'land' ? 1.1 : 1.05,
      );
    } else if (next === 'run' || next === 'holdRun') {
      nextAction.setLoop(THREE.LoopRepeat, Infinity);
      nextAction.clampWhenFinished = false;
      nextAction.setEffectiveTimeScale(next === 'holdRun' ? 1.2 : 1.05);
    } else {
      nextAction.setLoop(THREE.LoopRepeat, Infinity);
      nextAction.clampWhenFinished = false;
      nextAction.setEffectiveTimeScale(1);
    }

    if (this.currentAction && this.currentAction !== nextAction) {
      this.currentAction.fadeOut(FADE);
      nextAction.reset().setEffectiveWeight(1).fadeIn(FADE).play();
    } else {
      nextAction.reset().fadeIn(0.05).play();
    }
    this.currentAction = nextAction;
  }

  getState(): AnimState {
    return this.state;
  }

  /** Prefer exact clip; walk STATE_FALLBACK when Mixamo clip was not packed. */
  private resolveAction(state: AnimState): THREE.AnimationAction | undefined {
    let cursor: AnimState | undefined = state;
    const seen = new Set<AnimState>();
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      const action = this.actions.get(cursor);
      if (action) return action;
      cursor = STATE_FALLBACK[cursor];
    }
    return undefined;
  }

  update(dt: number, speed: number): void {
    if (this.takeHandTimer > 0) {
      this.takeHandTimer = Math.max(0, this.takeHandTimer - dt);
    }

    if (this.heldMode || this.takeHandTimer > 0) {
      this.holdWeight = Math.min(1, this.holdWeight + dt * 2.4);
    } else {
      this.holdWeight = Math.max(0, this.holdWeight - dt * 3.5);
    }

    if (this.mixamoReady && this.mixer) {
      this.mixer.update(dt);
      this.applyHandIk();
      if (this.glow) {
        this.glow.intensity = 1.1 + Math.sin(performance.now() * 0.003) * 0.35;
      }
      return;
    }

    // Procedural fallback poses
    const parts = this.procedural;
    if (!parts) return;

    const t = this.phase;
    const held =
      this.state === 'holdIdle' ||
      this.state === 'holdWalk' ||
      this.state === 'holdRun' ||
      this.state === 'takeHand';
    const swing =
      this.state === 'run' || this.state === 'holdRun'
        ? 1.15
        : this.state === 'walk' || this.state === 'holdWalk'
          ? 0.7
          : this.state === 'climb'
            ? 0.9
            : 0.15;
    const rate =
      this.state === 'run' || this.state === 'holdRun'
        ? 10
        : this.state === 'walk' || this.state === 'holdWalk'
          ? 6.5
          : this.state === 'climb'
            ? 5.5
            : 2.2;
    this.phase += dt * rate * (0.35 + Math.min(1.4, speed * 0.35 + 0.4));

    const idleBob = Math.sin(performance.now() * 0.002) * 0.015;
    parts.hips.position.y = 0.95 + idleBob;

    if (this.state === 'idle' || this.state === 'holdIdle') {
      this.poseIdle(parts, t);
      if (held) this.poseHeldArms(parts);
    } else if (
      this.state === 'walk' ||
      this.state === 'run' ||
      this.state === 'holdWalk' ||
      this.state === 'holdRun'
    ) {
      this.poseGait(parts, t, swing);
      if (held) this.poseHeldArms(parts);
    } else if (this.state === 'takeHand') {
      this.poseTakeHand(parts, 1 - this.takeHandTimer / TAKE_HAND_DURATION);
    } else if (this.state === 'jump' || this.state === 'fall' || this.state === 'climbOff') {
      this.poseAir(parts, this.state !== 'fall');
    } else if (this.state === 'climb') {
      this.poseClimb(parts, t);
    } else if (
      this.state === 'wallIdle' ||
      this.state === 'shimmyL' ||
      this.state === 'shimmyR'
    ) {
      this.poseWallShimmy(parts, t, this.state !== 'wallIdle');
    }

    if (this.glow) {
      this.glow.intensity = 1.1 + Math.sin(performance.now() * 0.003) * 0.35;
    }
  }

  /** Pull hold-hand toward a world target (call after setHandIkTarget). */
  refreshHandIk(): void {
    this.applyHandIk();
  }

  private applyHandIk(): void {
    if (!this.ikActive || this.holdWeight < 0.02) return;
    const chain = this.ikSide === 'left' ? this.leftArm : this.rightArm;
    if (!chain) return;
    solveTwoBoneIK(chain, this.ikTarget, this.holdWeight);
  }

  /** Procedural: extend the hold-side arm outward toward a partner. */
  private poseHeldArms(p: ProceduralParts): void {
    if (this.companion) {
      // Right arm toward player's left.
      p.rightArm.rotation.set(0.35, 0.15, -1.05);
      p.leftArm.rotation.set(0.2, 0, 0.15);
    } else {
      p.leftArm.rotation.set(0.35, -0.15, 1.05);
      p.rightArm.rotation.set(0.2, 0, -0.15);
    }
  }

  private poseTakeHand(p: ProceduralParts, u: number): void {
    const t = THREE.MathUtils.clamp(u, 0, 1);
    this.poseIdle(p, this.phase);
    if (this.companion) {
      p.rightArm.rotation.set(
        THREE.MathUtils.lerp(0.15, 0.45, t),
        THREE.MathUtils.lerp(0, 0.2, t),
        THREE.MathUtils.lerp(-0.12, -1.15, t),
      );
    } else {
      p.leftArm.rotation.set(
        THREE.MathUtils.lerp(0.15, 0.45, t),
        THREE.MathUtils.lerp(0, -0.2, t),
        THREE.MathUtils.lerp(0.12, 1.15, t),
      );
    }
  }

  private poseIdle(p: ProceduralParts, t: number): void {
    p.leftArm.rotation.set(0.15 + Math.sin(t) * 0.04, 0, 0.12);
    p.rightArm.rotation.set(0.15 + Math.cos(t) * 0.04, 0, -0.12);
    p.leftLeg.rotation.set(0, 0, 0.03);
    p.rightLeg.rotation.set(0, 0, -0.03);
    p.cloak.rotation.x = Math.PI + Math.sin(t * 0.5) * 0.03;
  }

  private poseGait(p: ProceduralParts, t: number, amp: number): void {
    const a = Math.sin(t) * amp;
    p.leftArm.rotation.set(-a * 0.9, 0, 0.1);
    p.rightArm.rotation.set(a * 0.9, 0, -0.1);
    p.leftLeg.rotation.set(a, 0, 0.02);
    p.rightLeg.rotation.set(-a, 0, -0.02);
    p.hips.position.y = 0.95 + Math.abs(Math.sin(t)) * 0.04;
    p.cloak.rotation.x = Math.PI + 0.08 + Math.abs(a) * 0.05;
  }

  private poseAir(p: ProceduralParts, up: boolean): void {
    p.leftArm.rotation.set(up ? -0.6 : 0.4, 0, 0.35);
    p.rightArm.rotation.set(up ? -0.55 : 0.35, 0, -0.35);
    p.leftLeg.rotation.set(up ? -0.35 : 0.5, 0, 0.08);
    p.rightLeg.rotation.set(up ? 0.2 : -0.15, 0, -0.08);
  }

  private poseClimb(p: ProceduralParts, t: number): void {
    const a = Math.sin(t) * 0.85;
    p.leftArm.rotation.set(-1.2 + a, 0, 0.4);
    p.rightArm.rotation.set(-1.2 - a, 0, -0.4);
    p.leftLeg.rotation.set(0.4 - a * 0.6, 0, 0.1);
    p.rightLeg.rotation.set(0.4 + a * 0.6, 0, -0.1);
    p.hips.position.y = 0.95 + Math.sin(t) * 0.03;
  }

  /** Pressed into a wall — arms out, slight crouch; sidestep sway when moving. */
  private poseWallShimmy(p: ProceduralParts, t: number, moving: boolean): void {
    const sway = moving ? Math.sin(t) * 0.35 : 0;
    p.leftArm.rotation.set(-0.35, 0.15, 0.85);
    p.rightArm.rotation.set(-0.35, -0.15, -0.85);
    p.leftLeg.rotation.set(0.15 + sway * 0.4, 0, 0.12);
    p.rightLeg.rotation.set(0.15 - sway * 0.4, 0, -0.12);
    p.hips.position.y = 0.9;
    p.cloak.rotation.x = Math.PI + 0.12;
  }

  static mixamoImportNotes(): string {
    return [
      '1. Sign in at mixamo.com; copy(localStorage.getItem("access_token")) → ~/.cache/ninjascrolls/mixamo_token',
      '2. python3 tools/mixamo_fetch.py --only takeshi   # (and kaede for companion)',
      '3. Build slim demo GLBs with Blender (include HoldIdle/HoldWalk from Carry/CarryWalk):',
      '   blender --background --python tools/build_mixamo_character.py -- \\',
      '     --base assets/source/mixamo/takeshi/character.fbx \\',
      '     --animations /path/to/Idle,Walk,...,HoldIdle,HoldWalk.fbx \\',
      '     --output demos/quiet-keep/public/models/hero.glb',
      '4. Drop hero.glb / companion.glb under public/models — CharacterRig loads them automatically.',
      '5. Mixamo has no couple Hold Hands walk; runtime IK joins LeftHand↔RightHand while held.',
    ].join('\n');
  }
}
